import React from "react";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { Order, OfficeConfig } from "@types";
import { formatTimeSlotRange } from "../../utils/timeSlot";

interface OrderRecapDetailsProps {
  order: Order;
  officeConfig?: OfficeConfig;
}

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  drop: "Dépose",
  install: "Installation",
  direct_drop: "Pas de porte",
  afterwork: "Afterwork",
  overflow_j1: "J+1",
};

const getArticleDeliveryTypeLabel = (article: {
  drop_article: boolean;
  install_article: boolean;
  direct_drop_article: boolean;
}) => {
  if (article.install_article) return DELIVERY_TYPE_LABELS.install;
  if (article.direct_drop_article) return DELIVERY_TYPE_LABELS.direct_drop;
  return DELIVERY_TYPE_LABELS.drop;
};

const getArticleDeliveryTypeClass = (article: {
  drop_article: boolean;
  install_article: boolean;
  direct_drop_article: boolean;
}) => {
  if (article.install_article) return "order-recap-article__type--install";
  if (article.direct_drop_article)
    return "order-recap-article__type--direct-drop";
  return "order-recap-article__type--drop";
};

const OrderRecapDetails = ({ order, officeConfig }: OrderRecapDetailsProps) => {
  dayjs.locale("fr");

  const formattedDate = React.useMemo(() => {
    if (!order.timeSlot?.date) return "";
    return dayjs(order.timeSlot.date).format("dddd D MMMM YYYY");
  }, [order.timeSlot?.date]);

  const formattedTimeRange = React.useMemo(() => {
    if (!order.timeSlot?.hour_from || !order.timeSlot?.hour_to) {
      if (order.timeSlot?.halfday === "morning") return "Matin";
      if (order.timeSlot?.halfday === "afternoon") return "Après-midi";
      return "";
    }

    return formatTimeSlotRange(order.timeSlot, "H[h]mm");
  }, [order.timeSlot]);

  const halfdayLabel = React.useMemo(() => {
    if (order.timeSlot?.halfday === "morning") return "Matin";
    if (order.timeSlot?.halfday === "afternoon") return "Après-midi";
    return "";
  }, [order.timeSlot?.halfday]);

  const fullAddress = React.useMemo(() => {
    const parts = [
      order.client?.address,
      order.client?.address_addition,
      order.client?.postal_code && order.client?.city
        ? `${order.client.postal_code} ${order.client.city}`
        : order.client?.postal_code || order.client?.city,
    ].filter(Boolean);
    return parts.join(", ");
  }, [order.client]);

  const clientName = React.useMemo(() => {
    return [order.client?.firstname, order.client?.lastname]
      .filter(Boolean)
      .join(" ");
  }, [order.client?.firstname, order.client?.lastname]);

  return (
    <div className="order-recap-details">
      {/* Success banner */}
      <div className="order-recap-details__success-banner">
        <div className="order-recap-details__success-icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        </div>
        <h2 className="order-recap-details__success-title">
          Commande confirmée
        </h2>
        {order.reference && (
          <div className="order-recap-details__reference">
            Réf. {order.reference}
          </div>
        )}
      </div>

      {/* Delivery Date & Time */}
      {order.timeSlot && (
        <div className="order-recap-details__card order-recap-details__card--delivery">
          <div className="order-recap-details__card-header">
            <svg
              className="order-recap-details__icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
            </svg>
            <span>Livraison</span>
          </div>
          <div className="order-recap-details__card-body">
            <div className="order-recap-details__date">{formattedDate}</div>
            <div className="order-recap-details__time-row">
              {halfdayLabel && (
                <span className="order-recap-details__halfday">
                  {halfdayLabel}
                </span>
              )}
              {formattedTimeRange && (
                <span className="order-recap-details__time-range">
                  {formattedTimeRange}
                </span>
              )}
            </div>
            {(order.timeSlot?.is_after_work || order.timeSlot?.is_next_day) && (
              <div className="order-recap-details__badges">
                {order.timeSlot.is_after_work && (
                  <span className="order-recap-details__badge order-recap-details__badge--afterwork">
                    🌙 Afterwork
                  </span>
                )}
                {order.timeSlot.is_next_day && (
                  <span className="order-recap-details__badge order-recap-details__badge--next-day">
                    📅 J+1
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Client Info */}
      {order.client && (
        <div className="order-recap-details__card order-recap-details__card--client">
          <div className="order-recap-details__card-header">
            <svg
              className="order-recap-details__icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
            <span>Informations client</span>
          </div>
          <div className="order-recap-details__card-body">
            {clientName && (
              <div className="order-recap-details__client-row">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#a0aec0">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <span className="order-recap-details__client-value order-recap-details__client-value--name">
                  {clientName}
                </span>
              </div>
            )}
            {fullAddress && (
              <div className="order-recap-details__client-row">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#a0aec0">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                </svg>
                <span className="order-recap-details__client-value">
                  {fullAddress}
                </span>
              </div>
            )}
            {order.client.floor > 0 && (
              <div className="order-recap-details__client-row">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#a0aec0">
                  <path d="M17 11V3H7v4H3v14h8v-4h2v4h8V11h-4zM7 19H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm4 4H9v-2h2v2zm0-4H9V9h2v2zm0-4H9V5h2v2zm4 8h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm4 12h-2v-2h2v2zm0-4h-2v-2h2v2z" />
                </svg>
                <span className="order-recap-details__client-value">
                  Étage {order.client.floor}
                </span>
              </div>
            )}
            {order.client.phone && (
              <div className="order-recap-details__client-row">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#a0aec0">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                </svg>
                <a
                  href={`tel:${order.client.phone}`}
                  className="order-recap-details__client-value order-recap-details__client-value--link"
                >
                  {order.client.phone}
                </a>
              </div>
            )}
            {order.client.email && (
              <div className="order-recap-details__client-row">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="#a0aec0">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                <a
                  href={`mailto:${order.client.email}`}
                  className="order-recap-details__client-value order-recap-details__client-value--link"
                >
                  {order.client.email}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Articles */}
      {order.articles && order.articles.length > 0 && (
        <div className="order-recap-details__card order-recap-details__card--articles">
          <div className="order-recap-details__card-header">
            <svg
              className="order-recap-details__icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z" />
            </svg>
            <span>Articles ({order.articles.length})</span>
          </div>
          <div className="order-recap-details__articles">
            {order.articles.map((article) => (
              <div key={article.id} className="order-recap-article">
                <span className="order-recap-article__quantity">
                  ×{article.quantity}
                </span>
                <div className="order-recap-article__info">
                  <span className="order-recap-article__name">
                    {article.name || "Article"}
                  </span>
                </div>
                <span
                  className={`order-recap-article__type ${getArticleDeliveryTypeClass(
                    article,
                  )}`}
                >
                  {getArticleDeliveryTypeLabel(article)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Store info */}
      {order.store_name && (
        <div className="order-recap-details__card order-recap-details__card--store">
          <div className="order-recap-details__card-header">
            <svg
              className="order-recap-details__icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z" />
            </svg>
            <span>Magasin</span>
          </div>
          <div className="order-recap-details__card-body">
            <div className="order-recap-details__store-name">
              {order.store_name}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderRecapDetails;
